/**
 * Status indicator component for showing system health.
 */

import React from 'react';
import { cn } from '../utils';

export type StatusType = 'active' | 'healthy' | 'valid' | 'inactive' | 'error' | 'warning' | 'pending';

export interface StatusIndicatorProps {
  /** Current status */
  status: StatusType;
  
  /** Label to display next to the indicator */
  label?: string;
  
  /** Size of the indicator */
  size?: 'sm' | 'md' | 'lg';
  
  /** Whether to show the pulse animation for active states */
  pulse?: boolean;
  
  /** Additional class names */
  className?: string;
}

const statusColors: Record<StatusType, { bg: string; dot: string; text: string }> = {
  active: {
    bg: 'bg-green-100',
    dot: 'bg-green-500',
    text: 'text-green-700',
  },
  healthy: {
    bg: 'bg-green-100',
    dot: 'bg-green-500',
    text: 'text-green-700',
  },
  valid: {
    bg: 'bg-green-100',
    dot: 'bg-green-500',
    text: 'text-green-700',
  },
  inactive: {
    bg: 'bg-gray-100',
    dot: 'bg-gray-400',
    text: 'text-gray-600',
  },
  error: {
    bg: 'bg-red-100',
    dot: 'bg-red-500',
    text: 'text-red-700',
  },
  warning: {
    bg: 'bg-yellow-100',
    dot: 'bg-yellow-500',
    text: 'text-yellow-700',
  },
  pending: {
    bg: 'bg-blue-100',
    dot: 'bg-blue-500',
    text: 'text-blue-700',
  },
};

const statusLabels: Record<StatusType, string> = {
  active: 'Active',
  healthy: 'Healthy',
  valid: 'Valid',
  inactive: 'Inactive',
  error: 'Error',
  warning: 'Warning',
  pending: 'Pending',
};

const sizeStyles = {
  sm: {
    container: 'px-2 py-0.5 text-xs',
    dot: 'w-1.5 h-1.5',
  },
  md: {
    container: 'px-2.5 py-1 text-sm',
    dot: 'w-2 h-2',
  },
  lg: {
    container: 'px-3 py-1.5 text-base',
    dot: 'w-2.5 h-2.5',
  },
};

/**
 * A status indicator badge component for displaying system health.
 */
export function StatusIndicator({
  status,
  label,
  size = 'md',
  pulse = true,
  className,
}: StatusIndicatorProps) {
  const colors = statusColors[status];
  const sizes = sizeStyles[size];
  const displayLabel = label ?? statusLabels[status];
  
  const shouldPulse = pulse && ['active', 'healthy', 'valid', 'pending'].includes(status);
  
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium',
        colors.bg,
        colors.text,
        sizes.container,
        className
      )}
    >
      <span className="relative flex">
        <span
          className={cn(
            'rounded-full',
            colors.dot,
            sizes.dot
          )}
        />
        {shouldPulse && (
          <span
            className={cn(
              'absolute inset-0 rounded-full opacity-75 animate-ping',
              colors.dot
            )}
          />
        )}
      </span>
      {displayLabel}
    </span>
  );
}

export interface StatusGroupProps {
  /** Status items to display */
  items: Array<{
    label: string;
    status: StatusType;
  }>;
  
  /** Orientation of the group */
  direction?: 'horizontal' | 'vertical';
  
  /** Additional class names */
  className?: string;
}

/**
 * A group of status indicators, useful for dashboard status displays.
 */
export function StatusGroup({
  items,
  direction = 'vertical',
  className,
}: StatusGroupProps) {
  return (
    <div
      className={cn(
        'flex gap-2',
        direction === 'vertical' ? 'flex-col' : 'flex-row flex-wrap',
        className
      )}
    >
      {items.map((item, index) => (
        <div
          key={index}
          className="flex items-center justify-between gap-4 py-2 px-3 bg-gray-50 rounded-lg"
        >
          <span className="text-gray-700 font-medium">{item.label}</span>
          <StatusIndicator status={item.status} size="sm" />
        </div>
      ))}
    </div>
  );
}

